import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

type DashboardSummary = {
  workoutsToday: number;
  unreadMessages: number;
  ctl: number;
  atl: number;
  tsb: number;
  upcomingBooking: { start_time: number; end_time: number } | null;
};

function getGreeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatBookingTime(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();

  const time = `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  if (sameDay) return `Today, ${time}`;
  if (isTomorrow) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}, ${time}`;
}

function signed(n: number): string {
  return n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
}

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 24,
  fontFamily: "Inter, system-ui, Arial",
  color: "#fff",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#1a1a1a",
  borderRadius: 12,
  padding: 20,
  border: "1px solid #2a2a2a",
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: "#fff",
  margin: "0 0 16px 0",
};

const metricsRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 16,
};

const metricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  backgroundColor: "#222",
  borderRadius: 8,
  padding: "16px 12px",
};

const metricValueStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: "#ff6b35",
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const loadLineStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 16px",
  backgroundColor: "#222",
  borderRadius: 8,
  fontSize: 14,
  color: "#ddd",
  marginTop: 4,
};

const loadItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: 1,
};

const loadLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 2,
};

const loadValueStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: "#fff",
};

const bookingLinkStyle: React.CSSProperties = {
  display: "block",
  marginTop: 4,
  padding: "12px 16px",
  backgroundColor: "#222",
  border: "1px solid #ff6b35",
  borderRadius: 8,
  color: "#ff6b35",
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 600,
};

const loadingStyle: React.CSSProperties = {
  color: "#888",
  fontSize: 14,
};

const errorStyle: React.CSSProperties = {
  color: "#ff9a9a",
  fontSize: 14,
};

export function HomePage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date();
  const greeting = getGreeting(today.getHours());

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const res = await api.get<DashboardSummary>(
          "/api/user/dashboard-summary",
        );
        if (alive) setData(res);
      } catch (e: unknown) {
        if (alive) {
          setError(e instanceof Error ? e.message : "Failed to load dashboard");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div style={containerStyle}>
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 4px 0", color: "#fff" }}>
          {greeting}, {user?.name || "Athlete"}
        </h1>
        <p style={{ fontSize: 14, color: "#888", margin: 0 }}>
          {formatDate(today)}
        </p>
      </div>

      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Today&apos;s Summary</h2>
        {loading ? (
          <div style={loadingStyle}>Loading...</div>
        ) : error ? (
          <div style={errorStyle}>{error}</div>
        ) : (
          <>
            <div style={metricsRowStyle}>
              <div style={metricStyle}>
                <span style={metricValueStyle}>{data?.workoutsToday ?? 0}</span>
                <span style={metricLabelStyle}>Workouts</span>
              </div>
              <Link
                to="/messages"
                style={{ ...metricStyle, textDecoration: "none" }}
              >
                <span style={metricValueStyle}>{data?.unreadMessages ?? 0}</span>
                <span style={metricLabelStyle}>Unread</span>
              </Link>
            </div>
            <div style={loadLineStyle}>
              <div style={loadItemStyle}>
                <span style={loadLabelStyle}>Form (CTL)</span>
                <span style={loadValueStyle}>{signed(data?.ctl ?? 0)}</span>
              </div>
              <div style={loadItemStyle}>
                <span style={loadLabelStyle}>Fatigue (ATL)</span>
                <span style={loadValueStyle}>{signed(data?.atl ?? 0)}</span>
              </div>
              <div style={loadItemStyle}>
                <span style={loadLabelStyle}>Freshness (TSB)</span>
                <span style={loadValueStyle}>{signed(data?.tsb ?? 0)}</span>
              </div>
            </div>
            {data?.upcomingBooking && (
              <Link to="/bookings" style={bookingLinkStyle}>
                Next session: {formatBookingTime(data.upcomingBooking.start_time)}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
